import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { IMqttMessage } from "ngx-mqtt";
import { BehaviorSubject, Observable, of, Subscription } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Gateway } from "src/components/shared-components/class/general-classes/gateway";
import { GatewaySetting } from "src/model/class/general classes/gateway-setting";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { GatewayKind, GatewayStatus } from "src/components/shared-components/enum/enumGateway";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { GenericUserData } from "src/model/interface/generic-user-data";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import {
  gateayUpdateFailedBusOperationAction,
  gatewayConnectionStatusChangedAction,
  gatewayLoadStartsAction,
  gatewaySettingsLoadStartsAction,
  gatewaySettingsUnsetListAction,
  gatewayUnsetListAction,
} from "src/state/actions/gateway.actions";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { listGateway, listGatewaySettings } from "src/state/selectors/gateway.selectors";
import { EventService } from "./event.service";
import { LoggerService } from "./logger.service";
import { MediaService } from "./media.service";
import { MqttClientService } from "./mqtt-client.service";

@Injectable({
  providedIn: "root",
})
export class GatewayService implements iEvent, iMqttHandling {
  private _logContext: string = "Gateway.service";

  //#region Subcriptions to "_store.select(...)" Observables
  private _selectCurrentUserSub: Subscription = null;
  //#endregion

  private _initialized: boolean = false;
  private _isUserAuthenticated: boolean = false;

  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;

  _eventSubscriptions: Array<Subscription> = null;

  constructor(
    private _mqttService: MqttClientService,
    private _http: HttpClient,
    private _eventService: EventService,
    private _store: Store,
    private _loggerService: LoggerService
  ) {
    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();

    this.subscribeEvents();

    this._selectCurrentUserSub = this._store
      .select(currentUserData)
      .pipe(
        filter((currentUserData: GenericUserData) => currentUserData !== null),
        map((currentUserData: GenericUserData) => !!currentUserData.currentUser)
      )
      .subscribe((isUserAuthenticated: boolean) => {
        this._isUserAuthenticated = isUserAuthenticated;
      });
  }

  ngOnDestroy() {
    this.unsubscribeEvents();
    this.unsubscribeStoreSelect();

    this.unsubscribeToMQTTAddresses();
  }

  //#region Subscribe to event-service observable
  public subscribeEvents(): void {

    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnComeBackOnlineMqtt.pipe(
        filter(() => this._isUserAuthenticated)
      ).subscribe(async () => {
        this.checkAndReloadGatewayList();
        this.checkAndReloadGatewaySettingsList();
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {

        await this.unsubscribeToMQTTAddresses();

        this._store.dispatch(gatewayUnsetListAction());
        this._store.dispatch(gatewaySettingsUnsetListAction());
        this._initialized = false;
      })
    );
  }

  public unsubscribeEvents(): void {
    for (const sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  public async Initialize(): Promise<boolean> {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "Initialize()",
      "Gateway initialize"
    );

    /* This assignation is used only in order to make sure that only the first invocation of the method proceed to try initialising the gateways list.
        The real and significant value of "_initialized" is given by "getGateway()".
    */
    this._store.dispatch(gatewayLoadStartsAction());
    this._store.dispatch(gatewaySettingsLoadStartsAction());

    this._initialized = true;
    return this._initialized;
  }

  //#region Mqtt addresses handling

  /** 
    CONTAINS SUBSCRIPTIONS TO THE FOLLOWING MQTT-ADDRESS:

    +Subscription to summary addresses to handle color of the gateway
    +Subscription to RESERVED_DB_ACCESSCONTROL_FAILEDOPERATION_COUNT to handle the number near the gateway
  */
  public async subscribeMQTTAddresses(listGateways: Array<Gateway>) {
    let mqttAddress: string = "";
    let mqttAddressSubcription: Subscription = null;
    let returnedObservable: Observable<IMqttMessage> = null;

    //#region Subscribing to gateway-update-addresses

    if (listGateways) {
      for (const gateway of listGateways) {
        mqttAddress = this.calculateMQTTAddress(gateway);

        if (mqttAddress != "") {
          returnedObservable = await this._mqttService.subscribeTopic(
            mqttAddress
          );

          mqttAddressSubcription = returnedObservable.subscribe(
            (message: IMqttMessage) => {
              this.updateGatewayConnectionStatus(message);
            }
          );

          await this.checkLocalAddressSubscriptionMap(
            mqttAddress,
            mqttAddressSubcription
          );
        }
      }
    }

    //#endregion

    //#region Subscribing to RESERVED_DB_ACCESSCONTROL_FAILEDOPERATION_COUNT

    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_ACCESSCONTROL_FAILEDOPERATION_COUNT;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);
    mqttAddressSubcription = returnedObservable.subscribe(
      (message: IMqttMessage) => {
        this.updateGatewayFailedBusOperationCountOnMessageReceived(message);
      }
    );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );

    //#endregion //
  }

  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      result = true;
    }
    return result;
  }

  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }

  /**
   * Check if the Gateway list has already been loaded (IE., it has been initialised)
   * @returns
   */
  private checkIsGatewayListLoaded(): Promise<boolean> {
    return this._store
      .select(listGateway)
      .pipe(
        take(1),
        map((listGatewayObject: GenericStoreApiData) => {
          return listGatewayObject?.loaded;
        })
      )
      .toPromise();
  }

  /**Check if the Gateway list has already been asked (either successfully or with error) from server at least once before; if  so, reload data*/
  private checkAndReloadGatewayList(): void {
    this._store
      .select(listGateway)
      .pipe(
        take(1),
        filter(
          (listGatewayObject: GenericStoreApiData) => listGatewayObject?.loaded
        )
      )
      .subscribe(async (listGatewayObject: GenericStoreApiData) => {
        this._initialized = await this.checkIsGatewayListLoaded();
        this._store.dispatch(gatewayLoadStartsAction());
      });
  }

  /**
   * Calculate the mqtt-address to subscribe by/un-subscribe from, based on the kind of gateway presented
   * @param gateway
   * @returns the complete mqtt-address, as string.
   */
  private calculateMQTTAddress(gateway: Gateway): string {
    let returnAddress: string = "";

    if (
      gateway.kind !== GatewayKind.UNKNOWN &&
      gateway.kind !== GatewayKind.VIRTUAL_VARIABLE &&
      gateway.kind !== GatewayKind.OMNIA &&
      gateway.kind !== GatewayKind.MASTER
    ) {
      returnAddress =
        AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
        "_" +
        gateway.instanceGUID +
        "/" +
        AppSettings.MIDDLE_CONNECTION_NAME +
        "/" +
        AppSettings.LOWER_SUMMARY_NAME;
    } else if (gateway.kind == GatewayKind.OMNIA) {
      returnAddress =
        AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
        "_" +
        "BIGOmnia/" +
        AppSettings.MIDDLE_CONNECTION_NAME +
        "/" +
        AppSettings.LOWER_TCP_NAME;
    } else if (gateway.kind == GatewayKind.MASTER) {
      returnAddress =
        AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
        "_" +
        "Master/" +
        AppSettings.MIDDLE_CONNECTION_NAME +
        "/" +
        AppSettings.LOWER_TCP_NAME;
    }

    return returnAddress;
  }

  //#endregion

  // Called when the failedBusOperationCount changes for a gateway
  // the reserverd send a value that is instanceGUIDChangedGateway_failedBusOperationCount (for each single gateway)
  private updateGatewayFailedBusOperationCountOnMessageReceived(
    message: IMqttMessage
  ): void {
    const messageInfo: Array<string> = JSON.parse(
      message.payload.toString()
    ).Message.Value.split("_");

    const instangeGuidUpdatedGateway = messageInfo[0];
    const failedBusOperationCount = parseInt(messageInfo[1], 10);

    this._store.dispatch(
      gateayUpdateFailedBusOperationAction({
        instanceGuid: instangeGuidUpdatedGateway,
        failedBusOperationCount,
      })
    );
  }

  // Called when the message about the change of connection of the gateway is received
  // - generic gateway: the value follows the GatewayStatus enum
  // - Master & Omnia: the value is only 0 or 1 (there is no device to connect)
  // Attention! The connection value Master is inverted: 0=connected, 1=not connected!
  private updateGatewayConnectionStatus(message: IMqttMessage): void {
    let gatewayStatusConnection: GatewayStatus = GatewayStatus.NOT_CONNECTED;
    let instanceGUIDChangedGateway = message.topic.split("_")[1].split("/")[0];
    const connectionStatusMessage: number = parseInt(
      JSON.parse(message.payload.toString()).Message.Value
    );

    if (instanceGUIDChangedGateway === "BIGOmnia") {
      instanceGUIDChangedGateway = AppSettings.INSTANCE_GUID_OMNIA;

      if (connectionStatusMessage === 1) {
        gatewayStatusConnection = GatewayStatus.CONNECTED;
      }
    } else if (instanceGUIDChangedGateway === "Master") {
      instanceGUIDChangedGateway = AppSettings.INSTANCE_GUID_MASTER;

      if (connectionStatusMessage === 0) {
        gatewayStatusConnection = GatewayStatus.CONNECTED;
      }
    } else {
      gatewayStatusConnection = connectionStatusMessage;
    }

    this._store.dispatch(
      gatewayConnectionStatusChangedAction({
        instanceGuid: instanceGUIDChangedGateway,
        status: gatewayStatusConnection,
      })
    );
  }

  /**
   * GF: Reload gateway configuration from API (with current status and current failed bus count)
   */
  public getGateway(): Observable<GenericApiData> {

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_GATEWAY
      )
      .pipe(
        switchMap((returnGateways: GenericApiData) => {
          returnGateways.data = returnGateways.data.map(
            (gateway: Gateway) => new Gateway(gateway)
          );

          this.subscribeMQTTAddresses(returnGateways.data);

          return of(returnGateways);
        })
      );

  }


  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectCurrentUserSub) {
      this._selectCurrentUserSub.unsubscribe();
    }
  }
  //#endregion

  //#region  GatewaySettings
  /**
   * Check if the Gateway settings list has already been loaded (IE., it has been initialised)
   * @returns
   */
  private checkIsGatewaySettingsListLoaded(): Promise<boolean> {
    return this._store
      .select(listGatewaySettings)
      .pipe(
        take(1),
        map((listGatewayObject: GenericStoreApiData) => {
          return listGatewayObject?.loaded;
        })
      )
      .toPromise();
  }

  /**Check if the Gateway settings list has already been asked (either successfully or with error) from server at least once before; if  so, reload data*/
  private checkAndReloadGatewaySettingsList(): void {
    this._store
      .select(listGatewaySettings)
      .pipe(
        take(1),
        filter(
          (listGatewayObject: GenericStoreApiData) => listGatewayObject?.loaded
        )
      )
      .subscribe(async (listGatewayObject: GenericStoreApiData) => {

        //TEMP
        this._initialized = await this.checkIsGatewaySettingsListLoaded();
        this._store.dispatch(gatewaySettingsLoadStartsAction());
      });
  }

  /**
   * GF: Reload gateway configuration from API (with current status and current failed bus count)
   */
  public getGatewaySettings(): Observable<GenericApiData> {

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_GATEWAY_SETTINGS
      )
      .pipe(
        switchMap((returnGateways: GenericApiData) => {
          return of(returnGateways);
        })
      );

  }


  /**
   * Get last modifed gateway settings
   *
   * @param gatewaySettings gateway settings store
   * @returns
   */
  public getGatewaySettingsByLastModified(
    gatewaySettings: GenericApiData
  ): Observable<GenericApiData> {
    const queryString: string =
      "?lastModified=" + encodeURIComponent(gatewaySettings.lastAPIModified);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_GATEWAY_SETTINGS + queryString
      )
      .pipe(
        map((returnAreaStore: GenericApiData) => {
          //if initial resource isn't changed it return null value (returnNotificationStore = null)
          if (returnAreaStore) {
            gatewaySettings = returnAreaStore;
          }

          return gatewaySettings;
        })
      );
  }

  /**
   * Save gateway setting on server
   * @param GatewaySetting gateway setting to update/insert
   * @param InstanceGUID InstanceGUID of the gateway
   * @returns 
   */
  public async saveGatewaySetting(GatewaySetting: GatewaySetting, InstanceGUID: string) {
    return this._http
      .put<GatewaySetting>(
        environment.httpServerRoot + AppSettings.API_URL_GATEWAY_SETTINGS + "?instanceguid=" + encodeURIComponent(InstanceGUID),
        GatewaySetting
      )
      .toPromise();
  }
  //#endregion
}
